import { useEffect, useRef, useState } from 'react';
import { ActionIcon, BackgroundImage, Box, Button, Center, Flex, Group, Image, Overlay, SimpleGrid, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSelector } from 'react-redux';
import { IRootState } from 'config/redux';
import { useTranslation, Trans } from 'react-i18next';
import { Carousel } from '@mantine/carousel';
import { useNavigate } from 'react-router-dom';
import Autoplay from 'embla-carousel-autoplay';
import { useMask } from '@react-input/mask';

import { BasicButton, Benefit, Card, CalculatorForm, Contact, Dialog, Footer, HeroBackground, NarrowSection, Review, Section, SectionText, SectionTitle, Step, TextInput, Text } from 'components';
import { AppConstants, ServiceConstants } from '../../constants';
import { ARROW_LEFT_ICON, ARROW_RIGHT_ICON, CARD_IMG, CHECK_FILLED_ICON, FAMILY_IMG, FAQ_IMGS, NUMBER_OF_STATS, NUMBER_OF_STEPS, NUMBER_OF_TESTIMONIALS, SWITZERLAND_IMG } from 'constants/app';
import { AppUtils } from 'utils';
import { CreditOptionsResponse } from 'types';
import { CreditService, PublicService } from 'services';
import classes from './index.module.css';
import { ROUTE_CREDIT_CARD, ROUTE_CREDIT_REQUEST_BASIC, ROUTE_FAQ } from 'constants/route';
import { IconPlus, IconTrash } from '@tabler/icons-react';

const {
  CREDIT_AMOUNT_INITIAL,
  CREDIT_CONSTANT_C_LOWER,
  CREDIT_CONSTANT_C_HIGHER,
  CREDIT_PAYMENT_INITIAL,
  MASK_SWISS_PHONE_NUMBER,
  NUMBER_OF_BENEFITS,
  PHONE_NUMBER_CODES,
  PHONE_NUMBER_DATA,
} = AppConstants;

const { REQUEST_CREDIT_OPTIONS_ID, REQUEST_RECOMMENDATIONS_ID } = ServiceConstants;

const initialValues = {
  creditAmount: CREDIT_AMOUNT_INITIAL,
  paymentPeriod: CREDIT_PAYMENT_INITIAL,
  monthlyInstallmentFrom: '',
  monthlyInstallmentTo: '',
};

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const creditOptionsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_OPTIONS_ID]);
  const recommendationsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_RECOMMENDATIONS_ID]);
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });
  const phoneNumberPartnerRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const [options, setOptions] = useState<CreditOptionsResponse | undefined>({} as CreditOptionsResponse);
  const [recommendationsSubmitted, setRecommendationsSubmitted] = useState<boolean>(false);

  const autoplay = useRef(Autoplay({ delay: 5000 }));
  const autoplay2 = useRef(Autoplay({ delay: 5000 }));

  const form = useForm({
    initialValues,
    onValuesChange: (values) => {
      populateMonthlyInstallment(values.creditAmount, values.paymentPeriod);
    }
  });

  useEffect(() => {
    CreditService.creditOptions();
    populateMonthlyInstallment(form.values.creditAmount, form.values.paymentPeriod);
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(creditOptionsServiceObject, REQUEST_CREDIT_OPTIONS_ID, setOptions);
  }, [creditOptionsServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(recommendationsServiceObject, REQUEST_RECOMMENDATIONS_ID, () => {
      setRecommendationsSubmitted(true);
      referralForm.reset();
    });
  }, [recommendationsServiceObject]);

  const populateMonthlyInstallment = (creditAmount: number, paymentPeriod: number) => {
    if (creditAmount && paymentPeriod) {
      const minLimit = AppUtils.getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_LOWER);
      const maxLimit = AppUtils.getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_HIGHER);

      form.setFieldValue('monthlyInstallmentFrom', minLimit.toString());
      form.setFieldValue('monthlyInstallmentTo', maxLimit.toString());
    }
  };

  const referralForm = useForm({
    initialValues: {
      fullName: '',
      phoneNumber: '',
      recommendations: [{ fullName: '', phoneNumber: '' }],
    },
    validate: {
      fullName: (value) => {
        if (!value) {
          return t('input.required');
        }
        if (value.split(' ').length !== 2) {
          return t('input.fullName.invalid');
        }
        return undefined;
      },
      phoneNumber: (value) => {
        if (!value) {
          return t('input.required');
        }
        if (!PHONE_NUMBER_CODES.some(code => value.startsWith(code) && PHONE_NUMBER_DATA[code]?.length.includes(value.replace(/\s/g, '').length))) {
          return t('input.phoneNumber.invalid');
        }
        return undefined;
      },
      recommendations: {
        fullName: (value) => {
          if (!value || value.trim().length === 0) {
            return t('input.required');
          }
          if (value.split(' ').length !== 2) {
            return t('input.fullName.invalid');
          }
          return undefined;
        },
        phoneNumber: (value) => {
          if (!value) {
            return t('input.required');
          }
          if (!PHONE_NUMBER_CODES.some(code => value.startsWith(code) && PHONE_NUMBER_DATA[code]?.length.includes(value.replace(/\s/g, '').length))) {
            return t('input.phoneNumber.invalid');
          }
          return undefined;
        },
      },
    },
  });

  const submitRecommendations = (values: any) => {
    const { countryCode, phoneNumber } = extractPhoneData(values.phoneNumber);
    const transformedRecommendations = values.recommendations.map((item: any) => {
      const { countryCode, phoneNumber } = extractPhoneData(item.phoneNumber);
      return {
        fullName: item.fullName,
        countryCode,
        phoneNumber,
      };
    });

    const result = {
      fullName: values.fullName,
      countryCode,
      phoneNumber,
      recommendations: transformedRecommendations,
    };
    PublicService.submitRecommendations(result);
  }

  const extractPhoneData = (phone: string) => {
    const countryCode = PHONE_NUMBER_CODES.find(code => phone.startsWith(code));
    const number = phone.slice(countryCode!.length);
    return { countryCode, phoneNumber: number };
  };

  const addFriend = () => {
    if (referralForm.values.recommendations.length < 3) {
      referralForm.insertListItem('recommendations', { fullName: '', phoneNumber: '' });
    }
  };

  const removeFriend = (index: number) => {
    referralForm.removeListItem('recommendations', index);
  };

  useEffect(() => {
    const shouldScroll = sessionStorage.getItem('scrollToContact');
    if (shouldScroll === 'true') {
      sessionStorage.removeItem('scrollToContact');

      const contactSection = document.getElementById('contact');
      if (contactSection) {
        const yOffset = 200;
        const y = contactSection.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      }
    }
  }, []);

  return (
    <>
      <HeroBackground image={FAMILY_IMG}>
        <Section hero={true}>
          <Flex direction={{ base: 'column', sm: 'row' }} gap={{ base: 40, sm: 60, lg: 120, xl: 230 }}>
            <Stack className={classes.infoWrapper}>
              <Group>
                <Image h='auto' w='auto' src={CHECK_FILLED_ICON} />
                <SectionText text={t('home.hero.preheading')}></SectionText>
              </Group>
              <Stack gap={28}>
                <SectionTitle title={
                  <>
                    <span>{t('home.hero.title.first')}</span>{' '}
                    <span className={classes.accentColor}>{t('home.hero.title.second')}</span>{' '}
                    <span>{t('home.hero.title.third')}</span>{' '}
                    <span className={classes.accentColor}>{t('home.hero.title.fourth')}</span>{' '}
                    <span>{t('home.hero.title.fifth')}</span>
                  </>
                } />
                <SectionText text={t('home.hero.description')} />
                <Stack gap={20}>
                  {Array(NUMBER_OF_BENEFITS).fill(null).map((_, index) => (
                    <Benefit key={index} text={t(`home.hero.benefits.${index}`)} />
                  ))}
                </Stack>
              </Stack>
            </Stack>
            <Box className={classes.calculatorWrapper}>
              <CalculatorForm
                t={t}
                form={form}
                options={options!}
                withButton={true}
                inputVariant={'dark'}
              />
            </Box>
          </Flex>
        </Section>
      </HeroBackground>
      <BackgroundImage src={SWITZERLAND_IMG} className={classes.background}>
        <Section>
          <SimpleGrid
            cols={{ base: 1, xs: 2, md: 3 }}
            spacing={{ base: 12, md: 32, lg: 50 }}
            className={classes.cards}
          >
            {Array(NUMBER_OF_STATS).fill(null).map((_, index) => (
              <Card
                key={index}
                label={t(`home.statistics.${index}.label`)}
                value={t(`home.statistics.${index}.value`)}
              />
            ))}
          </SimpleGrid>
        </Section>
      </BackgroundImage>
      <Section title={t('home.testimonials.title')} titleVariant={'dark'}>
        <Carousel
          slideSize={{ base: '90%', xl: '31%' }}
          slideGap={{ base: 20, md: 50 }}
          loop
          controlsOffset='0'
          nextControlIcon={<Image h='auto' w='auto' src={ARROW_RIGHT_ICON} />}
          previousControlIcon={<Image h='auto' w='auto' src={ARROW_LEFT_ICON} />}
          plugins={[autoplay2.current]}
          onMouseEnter={autoplay2.current.stop}
          onMouseLeave={autoplay2.current.reset}
          classNames={{ controls: classes.controls, control: classes.control, root: classes.root, viewport: classes.viewport }}
        >
          {Array(NUMBER_OF_TESTIMONIALS).fill(null).map((_, index) => (
            <Review
              key={index}
              author={t(`home.testimonials.reviews.${index}.author`)}
              message={t(`home.testimonials.reviews.${index}.message`)}
            />
          ))}

        </Carousel>
      </Section>
      <NarrowSection title={t('home.loan.title')}>
        <BasicButton onClick={() => navigate(ROUTE_CREDIT_REQUEST_BASIC)}>{t('action.more')}</BasicButton>
      </NarrowSection>
      <Section>
        <Flex justify='space-between' align='flex-start' className={classes.wrapper}>
          <Stack align='flex-start' className={classes.inner}>
            <Stack className={classes.innerHeader}>
              <SectionTitle
                title={
                  <>
                    <span>{t('home.credit_card.title.first')}</span>{' '}
                    <span className={classes.accentColor}>{t('home.credit_card.title.second')}</span>
                  </>
                }
                variant='dark' />
              <SectionText text={t('home.credit_card.description')} variant='light' />
            </Stack>
            <Stack gap={16} w='100%'>
              {Array(NUMBER_OF_STEPS).fill(null).map((_, index) => (
                <Step key={index} text={t(`home.credit_card.steps.${index}`)} />
              ))}
            </Stack>
            <BasicButton onClick={() => navigate(ROUTE_CREDIT_CARD)}>{t('home.credit_card.cta')}</BasicButton>
          </Stack>
          <Center className={classes.cardWrapper}>
            <Image h='auto' w='auto' src={CARD_IMG} className={classes.card} />
          </Center>
        </Flex>
      </Section>
      <Section title={t('home.q&a.title')} titleVariant='dark' center={true}>
        <Stack align='center' className={classes.faqWrapper}>
          <Carousel
            slideGap='lg'
            plugins={[autoplay.current]}
            onMouseEnter={autoplay.current.stop}
            onMouseLeave={autoplay.current.reset}
            loop
            withControls={false}
            withIndicators
            classNames={{ root: classes.faqRoot, container: classes.faqContainer, indicators: classes.faqIndicators, indicator: classes.faqIndicator }}>
            {FAQ_IMGS.map((image, index) => {
              return (
                <Carousel.Slide key={index}>
                  <BackgroundImage src={image.src} h={{ base: 200, md: 300, lg: 450 }} className={classes.faqImg}>
                    <Overlay className={classes.overlay} />
                    <Box className={classes.content}>
                      <Box className={classes.faqQuestion}>
                        <Trans
                          i18nKey={`home.faq.questions.${index}.question`}
                          components={{
                            highlight: <span className={classes.accent} />,
                          }}
                        />
                      </Box>
                      <Box className={classes.faqAnswer}>
                        <Trans
                          i18nKey={`home.faq.questions.${index}.answer`}
                          components={{
                            highlight: <span className={classes.accent} />,
                          }}
                        />
                      </Box>
                    </Box>
                  </BackgroundImage>
                </Carousel.Slide>

                
              );
            })}
          </Carousel>
          <BasicButton onClick={() => navigate(ROUTE_FAQ)}>{t('home.q&a.cta')}</BasicButton>
        </Stack>
      </Section>
      <NarrowSection title={t('home.referral.title')} backgroundImg={true}>
        <Dialog
          btnText={t('home.referral.cta')}
          title={t('home.referral.modal.title')}
          onClose={() => {
            referralForm.reset();
            setTimeout(() => setRecommendationsSubmitted(false), 500);
          }}
        >
          {recommendationsSubmitted ?
            <Box>
              <Text>{t('home.referral.modal.success.0')}</Text>
              <Text color={'#75AD53'}>{t('home.referral.modal.success.1')}</Text>
            </Box>
            :
            <form onSubmit={referralForm.onSubmit((values) => submitRecommendations(values))} className={classes.form}>
              <Stack gap={36} mb={16}>
                <Stack gap={16}>
                  <SectionText text={t('home.referral.modal.user.label')} variant='accent' />
                  <TextInput
                    variant='filled'
                    label={t('home.referral.modal.user.name.label')}
                    classNames={{ input: classes.modalInput }}
                    placeholder={t('home.referral.modal.user.name.placeholder')}
                    {...referralForm.getInputProps('fullName')}
                  />
                  <TextInput
                    label={t('home.referral.modal.user.phone.label')}
                    placeholder={t('home.referral.modal.user.phone.placeholder')}
                    classNames={{ input: classes.modalInput }}
                    {...referralForm.getInputProps('phoneNumber')}
                    ref={phoneNumberRef}
                  />
                </Stack>
                <Stack gap={16}>
                  <SectionText text={t('home.referral.modal.friend.label')} variant='accent' />
                  {referralForm.values.recommendations.map((_, index) => (
                    <Group justify='space-between' align='start' pos='relative' key={`referral-${index}`}>
                      <Stack gap={16} key={index} style={{ flex: 1 }}>
                        <TextInput
                          variant='filled'
                          label={t('home.referral.modal.friend.name.label')}
                          placeholder={t('home.referral.modal.friend.name.placeholder')}
                          classNames={{ input: classes.modalInput }}
                          {...referralForm.getInputProps(`recommendations.${index}.fullName`)}
                        />
                        <TextInput
                          label={t('home.referral.modal.friend.phone.label')}
                          placeholder={t('home.referral.modal.friend.phone.placeholder')}
                          classNames={{ input: classes.modalInput }}
                          {...referralForm.getInputProps(`recommendations.${index}.phoneNumber`)}
                          ref={phoneNumberPartnerRef}
                        />
                      </Stack>
                      {index !== 0 && (
                        <ActionIcon
                          onClick={() => removeFriend(index)}
                          variant='light'
                          color='red'
                          size='xs'
                          className={classes.removeFriend}
                          mt={4}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      )}
                    </Group>
                  ))}
                  {referralForm.values.recommendations.length < 3 && (
                    <Button
                      leftSection={<IconPlus size={14} stroke={3} />}
                      className={classes.addFriend}
                      variant='outline'
                      onClick={addFriend}
                    >
                      {t('home.referral.modal.additional')}
                    </Button>
                  )}
                </Stack>
              </Stack>
              <BasicButton mt={24} type='submit'>{t('home.referral.modal.cta')}</BasicButton>
            </form>
          }
        </Dialog>
      </NarrowSection >
      <Contact />
      <Footer />
    </>
  );
}

export default HomePage;